/**
 * ELEMENTAL Market Data Service
 * 
 * Standalone service - NO BACKEND REQUIRED
 * Fetches data directly from free public APIs
 * 
 * APIs Used:
 * - Yahoo Finance (stocks via AllOrigins proxy)
 * - CoinGecko (crypto - free, no key)
 * - Alternative.me (Fear & Greed - free, no key)
 * - Finnhub (news - free tier, optional key)
*/

// Optional: Add your free Finnhub API key for news (get one at finnhub.io)
const FINNHUB_API_KEY = 'd4tekr9r01qnn6lkepl0d4tekr9r01qnn6lkeplg';

// Cache to avoid hammering APIs
const cache = new Map();
const CACHE_DURATION = {
  quotes: 60 * 1000,         // 1 minute
  indices: 60 * 1000,        // 1 minute  
  fearGreed: 30 * 60 * 1000, // 30 minutes
  crypto: 60 * 1000,         // 1 minute
  news: 5 * 60 * 1000,       // 5 minutes
};

function getCached(key, maxAge) {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < maxAge) {
    return cached.data;
  }
  return null;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

// ============================================
// STOCK QUOTES - Yahoo Finance
// ============================================

/**
 * Fetch stock quote from Yahoo Finance
 * Uses AllOrigins proxy to avoid CORS
 */
export async function getQuote(symbol) {
  const cacheKey = `quote_${symbol}`;
  const cached = getCached(cacheKey, CACHE_DURATION.quotes);
  if (cached) return cached;

  try {
    // Yahoo Finance API endpoint
    const yahooUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${symbol}?interval=1d&range=1d`;
    
    // Use AllOrigins proxy to bypass CORS
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(yahooUrl)}`;
    
    const response = await fetch(proxyUrl);
    if (!response.ok) throw new Error('Failed to fetch');
    
    const data = await response.json();
    const result = data.chart?.result?.[0];
    
    if (!result) throw new Error('No data');
    
    const meta = result.meta;
    const quote = result.indicators?.quote?.[0];
    
    const currentPrice = meta.regularMarketPrice || meta.previousClose;
    const previousClose = meta.chartPreviousClose || meta.previousClose;
    const change = currentPrice - previousClose;
    const changePercent = (change / previousClose) * 100;
    
    const parsed = {
      symbol: meta.symbol,
      name: meta.shortName || meta.symbol,
      price: currentPrice,
      previousClose: previousClose,
      change: change,
      changePercent: changePercent,
      high: meta.regularMarketDayHigh || quote?.high?.[0],
      low: meta.regularMarketDayLow || quote?.low?.[0],
      volume: meta.regularMarketVolume,
      marketCap: meta.marketCap,
    };
    
    setCache(cacheKey, parsed);
    return parsed;
  } catch (error) {
    console.error(`Failed to fetch quote for ${symbol}:`, error);
    return null;
  }
}

/**
 * Get multiple stock quotes
 */
export async function getMultipleQuotes(symbols) {
  const results = await Promise.all(symbols.map(s => getQuote(s)));
  return results.filter(Boolean);
}

// ============================================
// MARKET INDICES
// ============================================

/**
 * Get major market indices
 */
export async function getMarketIndices() {
  const cacheKey = 'market_indices';
  const cached = getCached(cacheKey, CACHE_DURATION.indices);
  if (cached) return cached;

  const indices = [
    { symbol: '^GSPC', name: 'S&P 500' },
    { symbol: '^IXIC', name: 'NASDAQ' },
    { symbol: '^DJI', name: 'DOW' },
  ];

  try {
    const results = await Promise.all(
      indices.map(async ({ symbol, name }) => {
        const quote = await getQuote(symbol);
        if (quote) {
          return {
            name,
            symbol,
            value: quote.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            change: quote.changePercent >= 0 
              ? `+${quote.changePercent.toFixed(2)}%` 
              : `${quote.changePercent.toFixed(2)}%`,
            positive: quote.changePercent >= 0,
            price: quote.price,
            changePercent: quote.changePercent,
          };
        }
        return { name, symbol, value: '--', change: '--', positive: true };
      })
    );
    
    setCache(cacheKey, results);
    return results;
  } catch (error) {
    console.error('Failed to fetch indices:', error);
    return [
      { name: 'S&P 500', value: '--', change: '--', positive: true },
      { name: 'NASDAQ', value: '--', change: '--', positive: true },
      { name: 'DOW', value: '--', change: '--', positive: true },
    ];
  }
}

// ============================================
// MARKET MOVERS - Top Stocks
// ============================================

/**
 * Get top market movers
 */
export async function getMarketMovers() {
  const cacheKey = 'market_movers';
  const cached = getCached(cacheKey, CACHE_DURATION.quotes);
  if (cached) return cached;

  // Popular stocks to track
  const symbols = ['NVDA', 'TSLA', 'AAPL', 'AMD', 'MSFT', 'META', 'GOOGL', 'AMZN'];
  
  try {
    const quotes = await Promise.all(symbols.map(s => getQuote(s)));
    const validQuotes = quotes.filter(Boolean).map(q => ({
      symbol: q.symbol,
      name: q.name,
      price: q.price,
      change: q.change,
      changePercent: q.changePercent,
    }));
    
    // Sort by absolute change percent (biggest movers first)
    const sorted = validQuotes.sort((a, b) => 
      Math.abs(b.changePercent) - Math.abs(a.changePercent)
    );
    
    const result = sorted.slice(0, 7);
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Failed to fetch market movers:', error);
    return [];
  }
}

// ============================================
// CRYPTO - CoinGecko (Free, No Key)
// ============================================

/**
 * Get Bitcoin price
 */
export async function getBitcoinPrice() {
  const cacheKey = 'btc_price';
  const cached = getCached(cacheKey, CACHE_DURATION.crypto);
  if (cached) return cached;

  try {
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true'
    );
    const data = await response.json();
    
    const result = {
      name: 'BTC',
      symbol: 'BTC',
      value: `$${data.bitcoin.usd.toLocaleString()}`,
      price: data.bitcoin.usd,
      change: data.bitcoin.usd_24h_change >= 0
        ? `+${data.bitcoin.usd_24h_change.toFixed(2)}%`
        : `${data.bitcoin.usd_24h_change.toFixed(2)}%`,
      changePercent: data.bitcoin.usd_24h_change,
      positive: data.bitcoin.usd_24h_change >= 0,
    };
    
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Failed to fetch BTC:', error);
    return { name: 'BTC', value: '--', change: '--', positive: true };
  }
}

/**
 * Get top crypto movers
 */
export async function getCryptoMovers() {
  const cacheKey = 'crypto_movers';
  const cached = getCached(cacheKey, CACHE_DURATION.crypto);
  if (cached) return cached;

  try {
    const response = await fetch(
      'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=12&page=1&sparkline=false&price_change_percentage=24h'
    );
    const data = await response.json();

    const result = data.map(coin => ({
      symbol: coin.symbol.toUpperCase(),
      name: coin.name,
      price: coin.current_price,
      change: coin.price_change_24h,
      changePercent: coin.price_change_percentage_24h,
      marketCap: coin.market_cap,
      volume: coin.total_volume,
      image: coin.image,
    }));

    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Failed to fetch crypto:', error);
    return [];
  }
}

// ============================================
// FEAR & GREED INDEX - Alternative.me (Free)
// ============================================

/**
 * Get Fear & Greed Index (Crypto-based, but good market sentiment indicator)
 */
export async function getFearGreedIndex() {
  const cacheKey = 'fear_greed';
  const cached = getCached(cacheKey, CACHE_DURATION.fearGreed);
  if (cached) return cached;

  try {
    const response = await fetch('https://api.alternative.me/fng/?limit=1');
    const data = await response.json();
    
    const result = {
      value: parseInt(data.data[0].value),
      label: data.data[0].value_classification,
      timestamp: data.data[0].timestamp,
    };
    
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Failed to fetch Fear & Greed:', error);
    return { value: 50, label: 'Neutral' };
  }
}

// ============================================
// NEWS - Finnhub or Fallback
// ============================================

/**
 * Get market news
 */
export async function getMarketNews(limit = 5) {
  const cacheKey = 'market_news';
  const cached = getCached(cacheKey, CACHE_DURATION.news);
  if (cached) return cached;

  // If Finnhub API key is provided, use it
  if (FINNHUB_API_KEY) {
    try {
      const response = await fetch(
        `https://finnhub.io/api/v1/news?category=general&token=${FINNHUB_API_KEY}`
      );
      const data = await response.json();
      
      const news = data.slice(0, limit).map((item, i) => ({
        id: i + 1,
        title: item.headline,
        source: item.source,
        time: getTimeAgo(item.datetime * 1000),
        url: item.url,
        category: item.category || 'general',
        sentiment: 'neutral',
        image: item.image,
      }));
      
      setCache(cacheKey, news);
      return news;
    } catch (error) {
      console.error('Failed to fetch news from Finnhub:', error);
    }
  }

  // Fallback: Return curated static news (update periodically)
  return getStaticNews();
}

function getTimeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function getStaticNews() {
  return [
    { 
      id: 1, 
      title: 'Fed signals cautious approach to rate cuts amid sticky inflation', 
      source: 'Reuters', 
      time: '1h ago', 
      category: 'economy',
      sentiment: 'neutral'
    },
    { 
      id: 2, 
      title: 'NVIDIA continues AI dominance with record data center revenue', 
      source: 'Bloomberg', 
      time: '2h ago', 
      category: 'tech',
      sentiment: 'bullish'
    },
    { 
      id: 3, 
      title: 'Bitcoin consolidates near $90K as institutional interest grows', 
      source: 'CoinDesk', 
      time: '3h ago', 
      category: 'crypto',
      sentiment: 'bullish'
    },
    { 
      id: 4, 
      title: 'Tech earnings season kicks off with mixed results from mega-caps', 
      source: 'CNBC', 
      time: '4h ago', 
      category: 'tech',
      sentiment: 'neutral'
    },
    { 
      id: 5, 
      title: 'Oil prices stabilize as OPEC+ weighs production decisions', 
      source: 'WSJ', 
      time: '5h ago', 
      category: 'commodities',
      sentiment: 'neutral'
    },
  ];
}

// ============================================
// ECONOMIC CALENDAR
// ============================================

/**
 * Get economic calendar events
 * Note: For real economic calendar, you'd need a paid API like Trading Economics
 * This returns upcoming events based on typical schedule
 */
export async function getEconomicCalendar() {
  const cacheKey = 'economic_calendar';
  const cached = getCached(cacheKey, CACHE_DURATION.news);
  if (cached) return cached;

  // Generate dynamic dates based on current date
  const now = new Date();
  const events = [
    { 
      event: 'FOMC Meeting Minutes', 
      impact: 'high', 
      daysFromNow: 0,
      time: '2:00 PM',
      forecast: '-',
      previous: '-'
    },
    { 
      event: 'Initial Jobless Claims', 
      impact: 'medium', 
      daysFromNow: 1,
      time: '8:30 AM',
      forecast: '220K',
      previous: '218K'
    },
    { 
      event: 'CPI Inflation Data', 
      impact: 'high', 
      daysFromNow: 3,
      time: '8:30 AM',
      forecast: '2.7%',
      previous: '2.6%'
    },
    { 
      event: 'Retail Sales', 
      impact: 'medium', 
      daysFromNow: 5,
      time: '8:30 AM',
      forecast: '0.4%',
      previous: '0.3%'
    },
    { 
      event: 'Fed Interest Rate Decision', 
      impact: 'high', 
      daysFromNow: 7,
      time: '2:00 PM',
      forecast: '4.50%',
      previous: '4.75%'
    },
  ];

  const calendar = events.map((e, i) => {
    const eventDate = new Date(now);
    eventDate.setDate(eventDate.getDate() + e.daysFromNow);
    
    let dateStr;
    if (e.daysFromNow === 0) dateStr = 'Today';
    else if (e.daysFromNow === 1) dateStr = 'Tomorrow';
    else dateStr = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    return {
      id: i + 1,
      date: dateStr,
      time: e.time,
      event: e.event,
      impact: e.impact,
      forecast: e.forecast,
      previous: e.previous,
    };
  });

  setCache(cacheKey, calendar);
  return calendar;
}

// ============================================
// COMBINED DASHBOARD DATA
// ============================================

/**
 * Fetch all dashboard data in parallel
 */
export async function getDashboardData() {
  const [indices, btc, fearGreed, news, calendar, stockMovers, cryptoMovers] = await Promise.all([
    getMarketIndices(),
    getBitcoinPrice(),
    getFearGreedIndex(),
    getMarketNews(),
    getEconomicCalendar(),
    getMarketMovers(),
    getCryptoMovers(),
  ]);

  // Combine indices with BTC
  const allIndices = [...indices, btc];

  return {
    indices: allIndices,
    fearGreed,
    news,
    calendar,
    stockMovers,
    cryptoMovers,
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================
// CACHE CONTROLS
// ============================================

export function clearCache() {
  cache.clear();
}

export function clearCacheKey(key) {
  cache.delete(key);
}